import React, { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import block from 'bem-cn'
import dayjs from 'dayjs'

import { getCurrencySymbol } from 'shared/constants/currencies/getCurrencySymbol'
import { IGetUserPromotionsFilters } from 'shared/models/BonusSystem'
import { BonusPromotionStatus, IUserPromotion } from 'shared/models/Promotion'
import { useMediaMaxTablet } from 'shared/style/var'

import { selectLocaleDict } from 'features/locale/selectors'
import { selectLanguage } from 'features/userSettings/selectors'

import { getBonusInfo, getBonusValidateTerm } from '../__data'
import { BonusActionButton } from '../BonusActionButton/BonusActionButton'
import { BonusInfoCell } from '../BonusInfoCell/BonusInfoCell'
import { BonusProgress } from '../BonusProgress/BonusProgress'
import { CardHeader } from './CardHeader'

import './Card.scss'

type IFrontCardProps = {
  promotion: IUserPromotion
  filters: IGetUserPromotionsFilters
  onInfoClick: () => void
}

const b = block('bonus-card')

export const FrontCard: React.FC<IFrontCardProps> = ({ promotion, filters, onInfoClick }) => {
  const locale = useSelector(selectLocaleDict, shallowEqual).bonuses
  const lang = useSelector(selectLanguage, shallowEqual)
  const isMobile = useMediaMaxTablet()

  const hasFreeSpins = useMemo(() => !!promotion.freeSpinsCount, [promotion.freeSpinsCount])

  const isPromotionAvailable = useMemo(
    () => promotion.status.split('_').includes(BonusPromotionStatus.available),
    [promotion],
  )
  const isPromotionActive = useMemo(() => promotion.status === BonusPromotionStatus.active, [promotion])
  const isPromotionCompleted = useMemo(
    () =>
      promotion.status === BonusPromotionStatus.completed ||
      promotion.status === BonusPromotionStatus.available_completed,
    [promotion],
  )

  const currencySymbol = useMemo(
    () => getCurrencySymbol(promotion.promotionGrandAmountCurrency),
    [promotion.promotionGrandAmountCurrency],
  )

  const validityTerm = useMemo(
    () => getBonusValidateTerm({ promotion, locale, payloadFilters: filters }),
    [locale, promotion, filters],
  )

  const { front: infoByBonusName, img: bonusIcon } = useMemo(
    () => getBonusInfo({ promotion, isMobile, locale }),
    [locale, isMobile, promotion],
  )

  const dateEnd = dayjs(promotion.expirationDate)
  const currentDate = dayjs()

  const promotionIsExpired = currentDate.diff(dateEnd) >= 0

  return (
    <div className={b({ front: true })}>
      <CardHeader promotion={promotion} logoIcon={bonusIcon[lang]} onInfoClick={onInfoClick} />

      <div
        className={b('cells', {
          available: isPromotionAvailable,
          active: isPromotionCompleted || isPromotionAvailable,
        })}>
        <BonusInfoCell name={infoByBonusName.title} value={infoByBonusName.value} gridAreaName="cell1" />
        <BonusInfoCell
          name={infoByBonusName.firstColumnName}
          value={`X${promotion.wagerCoefficient}`}
          gridAreaName="cell2"
        />
        {!isPromotionAvailable && (
          <BonusInfoCell
            name={infoByBonusName.secondColumnName}
            value={`${promotion.wagerComplete} ${currencySymbol}`}
            gridAreaName="cell3"
          />
        )}
        {!isPromotionAvailable && infoByBonusName.wonBonusContent && (
          <BonusInfoCell name={locale.wonBonus} value={infoByBonusName.wonBonusContent} gridAreaName="cell4" />
        )}
        <BonusInfoCell name={locale.validityTerm} value={validityTerm} gridAreaName="cell5" />
      </div>

      <div className={b('bonus-actions')}>
        <BonusActionButton
          promotionIsExpired={promotionIsExpired}
          filters={filters}
          promotion={promotion}
          type="redirect"
          className={b('action-button', { redirect: true })}
        />

        <BonusActionButton
          promotionIsExpired={promotionIsExpired}
          filters={filters}
          promotion={promotion}
          type="action"
          className={b('action-button', { action: true })}
        />

        {(isPromotionCompleted || (!hasFreeSpins && isPromotionActive)) && (
          <BonusProgress
            promotionIsExpired={promotionIsExpired}
            promotion={promotion}
            className={b('progress')}
            needToWinBackText={infoByBonusName.needToWinBackText}
          />
        )}
      </div>
    </div>
  )
}
